import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ApiError,
  approve,
  bulkApprove,
  exportRegister,
  flag,
  formatDate,
  getRequests,
  getStats,
  getSubmission,
  reject,
  waitingFor,
  type AdminStats,
  type MemberStatus,
  type RequestRow,
} from '../lib/api'
import { Card, Empty, ErrorBox, Kpi, Spinner, StatusPill, WarnBox } from '../components/bits'
import { ConfirmModal, Pager, ReasonModal } from '../components/Modal'
import MemberDetail from '../components/MemberDetail'

const PER_PAGE = 25

const TABS: { label: string; value: MemberStatus | null }[] = [
  { label: 'All', value: null },
  { label: 'Pending', value: 'pending' },
  { label: 'Flagged', value: 'flagged' },
  { label: 'Approved', value: 'approved' },
]

/**
 * The verification queue — SCREENS.md §7, and the first thing built because it
 * is the only screen the Secretariat cannot do their job without.
 *
 * Acceptance criteria this implements:
 *  - Every decision writes an audit entry (a trigger does it; nothing here can
 *    skip it).
 *  - Reject requires a reason, and that reason is emailed to the applicant.
 *  - Duplicate KitNo or a >85% name+year match arrives pre-flagged with the
 *    matching record linked.
 *  - Bulk approve is limited to records with no flags.
 *  - CSV export is asynchronous and logged.
 */
export default function VerificationRequests() {
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [rows, setRows] = useState<RequestRow[]>([])
  const [tab, setTab] = useState<MemberStatus | null>('pending')
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [openRow, setOpenRow] = useState<RequestRow | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [page, setPage] = useState(1)

  /** Which row is awaiting a typed reason, and for which verdict. */
  const [reasonFor, setReasonFor] = useState<
    { row: RequestRow; kind: 'reject' | 'flag' } | null
  >(null)
  const [confirmBulk, setConfirmBulk] = useState(false)
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null)

  // 300 ms debounce, matching the member app's directory search.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300)
    return () => clearTimeout(t)
  }, [query])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [s, r] = await Promise.all([
        getStats(),
        getRequests({ status: tab, q: debouncedQuery, page, perPage: PER_PAGE }),
      ])
      setStats(s)
      setRows(r ?? [])
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [tab, debouncedQuery, page])

  useEffect(() => {
    void load()
  }, [load])

  // A filter change invalidates the page number.
  useEffect(() => {
    setPage(1)
  }, [tab, debouncedQuery])

  // Clear stale selections whenever the visible set changes, so a bulk action
  // can never apply to a row the reviewer is no longer looking at.
  useEffect(() => {
    setSelected(new Set())
  }, [tab, debouncedQuery])

  const total = rows[0]?.total ?? 0

  /** Only unflagged, non-duplicate pending rows are eligible for bulk approve. */
  const bulkEligible = useMemo(
    () =>
      rows.filter(
        (r) => r.status === 'pending' && !r.duplicate_of && selected.has(r.id),
      ),
    [rows, selected],
  )

  const selectedIneligible = selected.size - bulkEligible.length

  /**
   * Applies a verdict. The reason for a reject or flag is collected by
   * [ReasonModal] and passed in — `window.prompt` cannot validate before closing,
   * and this text is emailed to the applicant verbatim.
   */
  async function act(
    row: RequestRow,
    action: 'approve' | 'reject' | 'flag',
    reason = '',
  ): Promise<void> {
    if ((action === 'reject' || action === 'flag') && reason.trim() === '') {
      setReasonFor({ row, kind: action })
      return
    }

    setBusyId(row.id)
    setError(null)
    try {
      if (action === 'approve') await approve(row.id)
      if (action === 'reject') await reject(row.id, reason)
      if (action === 'flag') await flag(row.id, reason)
      setNotice(
        `${row.official_name} (KitNo ${row.kit_no}) — ${
          action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'flagged'
        }.`,
      )
      if (openRow?.id === row.id) setOpenRow(null)
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setBusyId(null)
    }
  }

  async function doBulkApprove(): Promise<void> {
    if (selected.size === 0) return
    setBusyId('bulk')
    setError(null)
    try {
      const result = await bulkApprove([...selected])
      const n = result.approved.length
      const skipped = result.skipped.length
      setNotice(
        skipped > 0
          ? `Approved ${n}. Skipped ${skipped} — ${result.skippedReason}.`
          : `Approved ${n} ${n === 1 ? 'record' : 'records'}.`,
      )
      setSelected(new Set())
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setBusyId(null)
    }
  }

  async function doExport(): Promise<void> {
    setBusyId('export')
    setError(null)
    setDownloadUrl(null)
    try {
      const result = await exportRegister({
        status: tab,
        note: `Export from the verification queue (${tab ?? 'all'})`,
      })
      setDownloadUrl(result.url)
      setNotice(
        `Exported ${result.rows} ${result.rows === 1 ? 'record' : 'records'} and ` +
          `logged it. The download link expires in ` +
          `${Math.round(result.expiresInSeconds / 60)} minutes.`,
      )
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setBusyId(null)
    }
  }

  function toggle(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const weekDelta =
    stats && stats.approvedThisWeek - stats.approvedLastWeek !== 0
      ? stats.approvedThisWeek - stats.approvedLastWeek
      : null

  return (
    <div className="stack">
      <div>
        <div className="eyebrow">Restricted access · Secretariat only</div>
        <h1 style={{ marginTop: 8 }}>Verification requests</h1>
        <p className="small" style={{ marginTop: 6 }}>
          New self-registrations awaiting checking against the CCP file record.
        </p>
      </div>

      <div className="row">
        <button
          className="btn btn-secondary"
          onClick={() => void doExport()}
          disabled={busyId === 'export'}
        >
          {busyId === 'export' ? 'Requesting…' : 'Export CSV'}
        </button>
        <button
          className="btn btn-accent"
          onClick={() => setConfirmBulk(true)}
          disabled={selected.size === 0 || busyId === 'bulk'}
          title={
            selected.size === 0
              ? 'Select some pending records first'
              : 'Flagged and duplicate records are skipped'
          }
        >
          {busyId === 'bulk'
            ? 'Approving…'
            : `Approve selected${selected.size > 0 ? ` (${selected.size})` : ''}`}
        </button>
        <div className="spacer" />
        <span className="small muted">
          {loading ? 'Loading…' : `${rows.length} of ${total} shown`}
        </span>
      </div>

      {selected.size > 0 && selectedIneligible > 0 && (
        <WarnBox>
          {selectedIneligible} of the {selected.size} selected{' '}
          {selectedIneligible === 1 ? 'record is' : 'records are'} flagged or a
          possible duplicate. Bulk approve will skip{' '}
          {selectedIneligible === 1 ? 'it' : 'them'} — decide{' '}
          {selectedIneligible === 1 ? 'it' : 'those'} individually against the
          file record.
        </WarnBox>
      )}

      {notice && (
        <Card style={{ borderColor: '#2f8fd4' }}>
          <div className="row">
            <span className="small">{notice}</span>
            <div className="spacer" />
            {downloadUrl && (
              <a
                className="btn btn-accent btn-sm"
                href={downloadUrl}
                download
                rel="noopener"
              >
                Download CSV
              </a>
            )}
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setNotice(null)
                setDownloadUrl(null)
              }}
            >
              Dismiss
            </button>
          </div>
        </Card>
      )}

      {error && <ErrorBox>{error}</ErrorBox>}

      {stats && (
        <div className="kpi-grid">
          <Kpi
            label="Pending requests"
            value={stats.pendingRequests}
            note={
              stats.avgDecisionDays == null
                ? 'No decisions recorded yet'
                : `Avg decision ${stats.avgDecisionDays} days`
            }
          />
          <Kpi
            label="Approved this week"
            value={stats.approvedThisWeek}
            note={
              weekDelta == null
                ? 'Same as last week'
                : `${weekDelta > 0 ? '+' : ''}${weekDelta} vs last week`
            }
            noteColor={weekDelta != null && weekDelta > 0 ? 'var(--approved)' : undefined}
          />
          <Kpi
            label="Flagged / duplicate"
            value={stats.flagged}
            note={stats.flagged > 0 ? 'Needs manual file check' : 'Nothing to check'}
            noteColor={stats.flagged > 0 ? 'var(--flagged)' : undefined}
          />
          <Kpi
            label="Total on register"
            value={stats.totalOnRegister}
            note={`${stats.countries} ${stats.countries === 1 ? 'country' : 'countries'}`}
          />
        </div>
      )}

      <Card>
        <div className="filter-row">
          <input
            className="input"
            style={{ maxWidth: 340 }}
            placeholder="Search by name, KitNo, house or email"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search verification requests"
          />
          {TABS.map((t) => (
            <button
              key={t.label}
              className={`tab ${tab === t.value ? 'active' : ''}`}
              onClick={() => setTab(t.value)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </Card>

      <div className="split">
        <Card style={{ padding: 'var(--s-5)' }}>
          {loading ? (
            <Spinner label="Loading the queue…" />
          ) : rows.length === 0 ? (
            <Empty
              title={debouncedQuery ? 'Nothing matches that search' : 'Nothing to review'}
              body={
                debouncedQuery
                  ? 'Try a different name, KitNo, house or email address.'
                  : 'There are no submissions in this state. New self-registrations will appear here as they arrive.'
              }
            />
          ) : (
            <table>
              <thead>
                <tr>
                  <th style={{ width: 34 }}>
                    <span className="small muted" aria-hidden>
                      —
                    </span>
                  </th>
                  <th>Petarian</th>
                  <th>House</th>
                  <th>Passed</th>
                  <th>Waiting</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Decision</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const decided = r.status === 'approved' || r.status === 'rejected'
                  return (
                    <tr key={r.id} className={openRow?.id === r.id ? 'selected' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.has(r.id)}
                          onChange={() => toggle(r.id)}
                          disabled={r.status !== 'pending'}
                          aria-label={`Select ${r.official_name}`}
                        />
                      </td>
                      <td>
                        <button
                          className="nav-item"
                          style={{ padding: 0, background: 'none' }}
                          onClick={() => setOpenRow(r)}
                        >
                          <span>
                            <strong style={{ color: 'var(--text)' }}>
                              {r.official_name}
                            </strong>
                            <br />
                            <span className="small muted">
                              KitNo {r.kit_no} · {r.email}
                            </span>
                          </span>
                        </button>
                        {r.duplicate_kit_no && (
                          <div
                            className="small"
                            style={{ color: 'var(--flagged)', marginTop: 4 }}
                          >
                            Possible duplicate of KitNo {r.duplicate_kit_no} (
                            {r.duplicate_name})
                          </div>
                        )}
                      </td>
                      <td>{r.house}</td>
                      {/* 0037 made passing_year required, so a null here now
                          means a record written before that — in practice the
                          one suspended demo account. The dash stays because
                          admin_requests still does not return the flag. */}
                      <td>{r.passing_year ?? '—'}</td>
                      <td className="small muted">{waitingFor(r.submitted_at)}</td>
                      <td>
                        <StatusPill status={r.status} />
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {decided ? (
                          <span className="small muted">
                            {r.status === 'approved' ? 'Approved' : 'Rejected'}
                          </span>
                        ) : (
                          <>
                            <button
                              className="btn btn-accent btn-sm"
                              onClick={() => void act(r, 'approve')}
                              disabled={busyId === r.id}
                            >
                              Approve
                            </button>{' '}
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => void act(r, 'reject')}
                              disabled={busyId === r.id}
                            >
                              Reject
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}

          <Pager
            page={page}
            perPage={PER_PAGE}
            total={total}
            busy={loading}
            onChange={setPage}
          />
        </Card>

        <SubmissionPanel
          row={openRow}
          busy={busyId === openRow?.id}
          onClose={() => setOpenRow(null)}
          onApprove={() => openRow && void act(openRow, 'approve')}
          onReject={() => openRow && void act(openRow, 'reject')}
          onFlag={() => openRow && void act(openRow, 'flag')}
        />
      </div>

      {reasonFor && (
        <ReasonModal
          kind={reasonFor.kind}
          memberName={reasonFor.row.official_name}
          busy={busyId === reasonFor.row.id}
          onCancel={() => setReasonFor(null)}
          onConfirm={(reason) => {
            const { row, kind } = reasonFor
            setReasonFor(null)
            void act(row, kind, reason)
          }}
        />
      )}

      {confirmBulk && (
        <ConfirmModal
          title={`Approve ${selected.size} ${selected.size === 1 ? 'record' : 'records'}?`}
          body={
            selectedIneligible > 0
              ? `${bulkEligible.length} will be approved. ${selectedIneligible} flagged or duplicate ${selectedIneligible === 1 ? 'record' : 'records'} will be skipped and must be decided individually.`
              : `All ${selected.size} will be approved against the CCP file record. Each approval is written to the audit log.`
          }
          confirmLabel="Approve them"
          busy={busyId === 'bulk'}
          onCancel={() => setConfirmBulk(false)}
          onConfirm={() => {
            setConfirmBulk(false)
            void doBulkApprove()
          }}
        />
      )}
    </div>
  )
}

/**
 * The submission detail panel: every value the Secretariat has to check against
 * the CCP file record, in the order the paper record lists them.
 *
 * Secretariat callers receive the unredacted record, so a member's privacy
 * settings cannot hide the fields being verified.
 */
function SubmissionPanel({
  row,
  busy,
  onClose,
  onApprove,
  onReject,
  onFlag,
}: {
  row: RequestRow | null
  busy: boolean
  onClose: () => void
  onApprove: () => void
  onReject: () => void
  onFlag: () => void
}) {
  const [detail, setDetail] = useState<Record<string, unknown> | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!row) {
      setDetail(null)
      return
    }
    let cancelled = false
    setLoading(true)
    getSubmission(row.kit_no)
      .then((d) => {
        if (!cancelled) setDetail(d)
      })
      .catch(() => {
        if (!cancelled) setDetail(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [row])

  if (!row) {
    return (
      <Card>
        <h3>Submission detail</h3>
        <p className="small" style={{ marginTop: 8 }}>
          Select a Petarian to see the values to check against the CCP file
          record.
        </p>
      </Card>
    )
  }

  return (
    <Card>
      <div className="row" style={{ marginBottom: 'var(--s-5)' }}>
        <h3>{row.official_name}</h3>
        <div className="spacer" />
        <button className="btn btn-secondary btn-sm" onClick={onClose}>
          Close
        </button>
      </div>

      {row.duplicate_kit_no && (
        <div style={{ marginBottom: 'var(--s-5)' }}>
          <WarnBox>
            Pre-flagged: this looks like KitNo {row.duplicate_kit_no} (
            {row.duplicate_name}) — same passing year and a name match above 85%.
            Check the file record before approving either.
          </WarnBox>
        </div>
      )}

      {loading ? (
        <Spinner label="Loading the submission…" />
      ) : (
        <>
          <div className="eyebrow" style={{ marginBottom: 8 }}>
            Check against the file record
          </div>
          <p className="small muted" style={{ margin: '0 0 4px' }}>
            Submitted {formatDate(row.submitted_at)}.
          </p>

          {/* Everything the member gave us, not the thirteen fields this
              panel used to show. Each section hides itself when empty. */}
          <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 4 }}>
            <MemberDetail detail={detail} />
          </div>

          <div
            className="stack"
            style={{ marginTop: 'var(--s-6)', gap: 'var(--s-2)' }}
          >
            <button
              className="btn btn-accent"
              onClick={onApprove}
              disabled={busy || row.status === 'approved'}
            >
              Approve this record
            </button>
            <div className="row" style={{ gap: 'var(--s-2)' }}>
              <button
                className="btn btn-secondary"
                style={{ flex: 1 }}
                onClick={onFlag}
                disabled={busy}
              >
                Flag
              </button>
              <button
                className="btn btn-danger"
                style={{ flex: 1 }}
                onClick={onReject}
                disabled={busy}
              >
                Reject
              </button>
            </div>
            <p className="small muted" style={{ margin: 0 }}>
              Rejecting requires a reason, which is emailed to the applicant.
              Every decision is written to the audit log.
            </p>
          </div>
        </>
      )}
    </Card>
  )
}
